import { Box, Button, Col, Modal, Row } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ICheckedTreatment } from '@common/interfaces/checkedTreatment';
import type {
    ITherapielandTreatment,
    ITherapielandTreatmentCategory,
} from '../../../../../../common/utils/therapielandTreatments';
import type { ValidationErrors } from '../../../../../utils/validation';
import { TherapielandTreatmentCategory } from '../../../../../../common/enums/therapielandTreatmentCategory';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { BadgeWithLabel } from '../../../../elements/BadgeWithLabel';
import { EnumDropdown, InputCheckbox, NullableInputText } from '../../../../elements/common';
import { GgzbTherV1IndexListModal } from './GgzbTherV1IndexListModal';
import { GgzbTherV1ThemeButtons } from './GgzbTherV1ThemeButtons';

interface IGgzbTherV1TreatmentListModalProps {
    checkDuplicates: (treatments: ICheckedTreatment[]) => Promise<boolean | undefined>;
    checkedTreatments: ICheckedTreatment[];
    errors: ValidationErrors;
    onCloseModal: () => void;
    onSave: (treatments: ICheckedTreatment[]) => Promise<void>;
    organizationId: string;
    selectedPractitionerId: string | null;
    showModal: boolean;
}

const osasExclusiveTherapieLandModulesNames = ['Slaapapneu'];

export function GgzbTherV1TreatmentListModal(props: IGgzbTherV1TreatmentListModalProps) {
    const [treatments, setTreatments] = useState<ITherapielandTreatment[]>([]);
    const [checkedTreatmentsInModal, setCheckedTreatmentsInModal] = useState<ICheckedTreatment[]>([]);
    const [hasDuplicates, setHasDuplicates] = useState(false);
    const [searchText, setSearchText] = useState<string | null>(null);
    const [showIndexModal, setShowIndexModal] = useState(false);
    const [activeTreatmentCategory, setActiveTreatmentCategory] = useState(TherapielandTreatmentCategory.volwassenen);

    useEffect(() => {
        getTreatments();
    }, [props.selectedPractitionerId]);

    useEffect(() => {
        setCheckedTreatmentsInModal([...props.checkedTreatments]);
    }, [props.checkedTreatments]);

    useEffect(() => {
        checkDuplicatesForCheckedTreatmentsInModal();
    }, [checkedTreatmentsInModal]);

    if (!props.showModal) {
        return null;
    }

    return (
        <Modal
            isOpen
            onClose={props.onCloseModal}
            size="large"
        >
            <Modal.Header>Therapieland programma's</Modal.Header>
            <Modal.CloseButton />
            <Modal.Body>
                <Box mb={6}>
                    <Row
                        columnGap={[2, 4]}
                        rowGap={[2, 4]}
                    >
                        <Col span={[12, null, 6]}>
                            <NullableInputText
                                onChange={(value) => setSearchText(value)}
                                placeholder={translateKey('therapieland.typeToFindTreatments')}
                                trimValue
                                value={searchText}
                            />
                        </Col>
                        <Col span={[12, null, 3]}>
                            <EnumDropdown
                                jsEnum={TherapielandTreatmentCategory}
                                noSelectPlaceHolder
                                onChange={(value) =>
                                    setActiveTreatmentCategory(value || TherapielandTreatmentCategory.volwassenen)
                                }
                                value={activeTreatmentCategory}
                            />
                        </Col>
                        <Col span={[12, null, 3]}>
                            <Button
                                iconLeft="jobs"
                                onClick={async () => await setShowIndexModal(!showIndexModal)}
                                variant="link"
                            >
                                Inhoudsopgave
                            </Button>
                        </Col>
                    </Row>
                </Box>
                <GgzbTherV1ThemeButtons
                    setCheckedTreatments={(checkedTreatments: ICheckedTreatment[], idsToUncheck: number[]) =>
                        setCheckedTreatments(checkedTreatments, idsToUncheck)
                    }
                    treatments={treatments}
                    treatmentsVisible={visibleTreatments(activeTreatmentCategory)}
                />
                <div style={{ columns: '270px 5' }}>{getTreatmentsFormGroup(activeTreatmentCategory)}</div>
            </Modal.Body>
            <Modal.Footer>
                {checkedTreatmentsInModal.length > 0 && (
                    <BadgeWithLabel
                        color="neutral"
                        label={translateKey('common.isSelected')}
                        value={checkedTreatmentsInModal.length.toString()}
                    />
                )}
                <Button
                    onClick={props.onCloseModal}
                    variant="outline"
                >
                    {translateKey('common.cancel')}
                </Button>
                <Button
                    isDisabled={hasDuplicates}
                    onClick={async () => await props.onSave(checkedTreatmentsInModal)}
                >
                    {translateKey('therapieland.saveTreatments')}
                </Button>
            </Modal.Footer>
            <GgzbTherV1IndexListModal
                setShowModal={setShowIndexModal}
                showModal={showIndexModal}
            />
        </Modal>
    );

    function getTreatmentsFormGroup(therapielandTreatmentCategory: TherapielandTreatmentCategory) {
        return (
            <>
                {visibleTreatments(therapielandTreatmentCategory).map((treatment) => (
                    <InputCheckbox
                        checked={checkedTreatmentsInModal.some(
                            (checkedTreatment: ICheckedTreatment) => checkedTreatment.id === treatment.id
                        )}
                        error={props.errors.get(`customControlData.checkedTreatments.${treatment.id}`)}
                        key={treatment.id}
                        onChange={(value) => onChange(value, treatment)}
                        text={
                            <span className="custom-control-description">
                                {addBoldTextToTreatmentName(treatment.name)}
                            </span>
                        }
                    />
                ))}
            </>
        );
    }

    function visibleTreatments(therapielandTreatmentCategory: TherapielandTreatmentCategory) {
        return filterTreatmentsWithSearchText().filter(
            (treatment) => getCategoryName(treatment.categories) === therapielandTreatmentCategory
        );
    }

    async function getTreatments() {
        if (!props.selectedPractitionerId) {
            return;
        }
        const selectedPractitionerId = props.selectedPractitionerId;
        const url = `/api/therapieland/getTreatments?practitionerId=${selectedPractitionerId}&organizationId=${props.organizationId}`;
        const result = await fetchJson(url, 'GET');
        if (result.json) {
            setTreatments(
                result.json.filter(
                    (treatment: ITherapielandTreatment) =>
                        !osasExclusiveTherapieLandModulesNames.includes(treatment.name)
                )
            );
        }
    }

    function filterTreatmentsWithSearchText() {
        if (searchText) {
            return treatments.filter(
                (treatment) => treatment.name.toLowerCase().search(searchText.toLowerCase()) !== -1
            );
        }

        return treatments;
    }

    function addBoldTextToTreatmentName(treatmentName: string) {
        if (searchText) {
            const startBoldSubstring = treatmentName.toLowerCase().search(searchText.toLowerCase());
            const endBoldSubstring = startBoldSubstring + searchText.length;

            return (
                <>
                    {treatmentName.substring(0, startBoldSubstring)}
                    <b>{treatmentName.substring(startBoldSubstring, endBoldSubstring)}</b>
                    {treatmentName.substring(endBoldSubstring, treatmentName.length)}
                </>
            );
        }

        return treatmentName;
    }

    function onChange(value: boolean, treatment: ITherapielandTreatment) {
        if (value) {
            setCheckedTreatments(
                [{ id: treatment.id, treatmentName: treatment.name, category: getCategoryName(treatment.categories) }],
                []
            );
        } else {
            setCheckedTreatments([], [treatment.id]);
        }
    }

    function setCheckedTreatments(treatmentsToCheck: ICheckedTreatment[], idsToUncheck: number[]) {
        let checkedTreatments = [...checkedTreatmentsInModal];
        if (idsToUncheck.length) {
            checkedTreatments = checkedTreatments.filter((treatment) => !idsToUncheck.includes(treatment.id));
        }
        if (treatmentsToCheck.length) {
            treatmentsToCheck = treatmentsToCheck.filter(
                (treatmentToCheck) =>
                    !checkedTreatments
                        .map((checkedTreatmentInModal) => checkedTreatmentInModal.id)
                        .includes(treatmentToCheck.id)
            );
            checkedTreatments.push(...treatmentsToCheck);
        }
        setCheckedTreatmentsInModal(checkedTreatments);
    }

    async function checkDuplicatesForCheckedTreatmentsInModal() {
        const hasDuplicateResults = await props.checkDuplicates(checkedTreatmentsInModal);
        setHasDuplicates(hasDuplicateResults || false);
    }
}

export function getCategoryName(categories: ITherapielandTreatmentCategory[]) {
    if (categories.length === 1) {
        return categories[0].name;
    } else if (categories.some((category) => category.name === TherapielandTreatmentCategory.volwassenen)) {
        return TherapielandTreatmentCategory.volwassenen;
    } else if (categories.some((category) => category.name === TherapielandTreatmentCategory.kindJeugd)) {
        return TherapielandTreatmentCategory.kindJeugd;
    } else if (categories.some((category) => category.name === TherapielandTreatmentCategory.lvb)) {
        return TherapielandTreatmentCategory.lvb;
    } else {
        throw Error("Something went wrong retrieving the treatment's category");
    }
}
